import React, { useMemo } from 'react';
import useBundlePricing from '../../../hooks/useBundlePricing';
import { priceFormat } from '../../../helpers/calculateDiscountAndCredits';
import icons from './icons';
import BundlePrice from './components/BundlePrice/BundlePrice';
import BundleLabel from './components/BundleLabel/BundleLabel';
import BundleTicketBlock from './components/BundleTicketBlock';
import './styles.scss';

const getPrizeTitle = (raffle) =>
  (raffle && (raffle.title || raffle.bundleTitle)) || "";

// 2500 → 800, 250 → 80, 900 → 300, 360 → 120
const getCrossedTicketCount = (count) => {
  if (!count) return null;
  const divided = count / 3;
  const step = count >= 1000 ? 100 : count >= 100 ? 10 : 1;
  return Math.floor(divided / step) * step;
};

const SingleBundleCard = ({
  isDouble,
  is341 = false,
  ticketsCount,
  ticketBundle,
  prize,
  generalSettings,
  discountRates,
  activeTicket,
  activeRaffles,
  discount,
  loyalty = null,
  isLandingTicket,
  landingTicket,
  loading,
  isExpired,
  onBuy,
  bonusDraw,
}) => {
  const isActiveRaffle = !!(activeRaffles && activeRaffles.length > 0);
  const isDisabled = !isActiveRaffle || loading || isExpired;

  const isLoyaltyActive = loyalty && loyalty.isActive;
  const hasMultiplier = isLoyaltyActive && loyalty.multiplier > 1;
  const loyaltyTickets =
    isLoyaltyActive && loyalty.bonusTickets
      ? loyalty.bonusTickets * (isDouble ? 2 : 1)
      : 0;
  const hasBonusTickets = loyaltyTickets > 0;
  const hasLoyaltyExtras = hasMultiplier || hasBonusTickets;

  const {
    totalTickets,
    temporaryDiscountAmount,
  } = useBundlePricing({
    ticketsCount,
    prize,
    discountRates,
    discount,
    generalSettings,
    isLandingTicket,
    landingTicket,
    isDoubleTickets: isDouble,
  });

  if (totalTickets < 15) {
    is341 = false;
  }

  const finalPrice = ticketBundle
    ? ticketBundle.price * ticketBundle.tickets
    : 0;
  const oldPrice = finalPrice;
  const formattedFinal = priceFormat(Math.ceil(finalPrice));

  const totalPrizeTickets = totalTickets * (isDouble ? 2 : 1);

  const loyaltyBonusBaseCount = useMemo(() => {
    if (hasMultiplier)
      return Math.ceil(totalTickets * (loyalty.multiplier - 1));
    if (hasBonusTickets) return loyalty.bonusTickets;
    return 0;
  }, [hasMultiplier, hasBonusTickets, totalTickets, loyalty]);

  const loyaltyBonusCount = loyaltyBonusBaseCount * (isDouble ? 2 : 1);

  const firstPrizeTitle = getPrizeTitle(activeRaffles && activeRaffles[0]);
  const secondPrizeTitle = isDouble
    ? getPrizeTitle(activeRaffles && activeRaffles[1])
    : "";

  const ticketSections = useMemo(() => {
    const sections = [
      {
        id: "primary",
        icon: icons.home,
        count: totalTickets,
        title: firstPrizeTitle,
        oldCount: is341 ? getCrossedTicketCount(totalTickets) : null,
      },
    ];

    if (isDouble) {
      sections.push({
        id: "secondary",
        icon: icons.extraHome,
        count: totalTickets,
        title: secondPrizeTitle,
        oldCount: is341 ? getCrossedTicketCount(totalTickets) : null,
      });
    }

    if (hasLoyaltyExtras) {
      sections.push({
        id: "loyalty",
        icon: icons.star,
        count: loyaltyBonusCount,
        countLabel: isDouble
          ? `${loyaltyBonusBaseCount} + ${loyaltyBonusBaseCount} Tickets`
          : undefined,
        title: "Loyalty bonus",
        oldCount: null,
      });
    }

    return sections;
  }, [
    totalTickets,
    firstPrizeTitle,
    secondPrizeTitle,
    isDouble,
    hasLoyaltyExtras,
    loyaltyBonusCount,
    loyaltyBonusBaseCount,
    is341,
  ]);

  const buttonId = `buy-now-${Math.round(finalPrice)}-${totalTickets}`;

  const isBundleChosen = activeTicket === ticketsCount;

  const handleBuy = () => {
    if (isDisabled || !onBuy) return;
    const bonusDrawId = bonusDraw && bonusDraw._id ? bonusDraw._id : null;
    onBuy({
      numOfTickets: ticketsCount,
      prizeType: "raffle",
      tickets: totalTickets,
      bonusDraw: bonusDrawId,
    })();
  };

  return (
    <div
      className={[
        "single-bundle-card-wrap",
        is341 && "single-bundle-card-wrap--341",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={[
          "single-bundle-card",
          isBundleChosen && "single-bundle-card--chosen",
          isDisabled && "single-bundle-card--disabled",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={isDisabled ? undefined : handleBuy}
      >
        <BundleLabel ticketsCount={ticketsCount} is341={is341} />

        {hasLoyaltyExtras && (
          <div className="single-bundle-card__multiplier">
            <span className="single-bundle-card__multiplier-value">
              {hasMultiplier ? `${loyalty.multiplier}X` : `+${loyaltyTickets}`}
            </span>
            {hasBonusTickets && (
              <span className="single-bundle-card__multiplier-text">
                tickets
              </span>
            )}
          </div>
        )}

        <div className="single-bundle-card__bg">
          <span />
          <span />
        </div>

        <div className="single-bundle-card__body">
          <div
            className={`single-bundle-card__tickets ${!!temporaryDiscountAmount && "single-bundle-card__tickets--discount"} ${is341 && "single-bundle-card__tickets--341"}`}
          >
            {ticketSections.map((section, sectionIndex) => (
              <React.Fragment key={section.id}>
                {sectionIndex > 0 && (
                  <div className="single-bundle-card__ticket-separator">
                    {icons.plusIcon}
                  </div>
                )}
                <BundleTicketBlock
                  icon={section.icon}
                  count={section.count}
                  countLabel={section.countLabel}
                  title={section.title}
                  oldCount={section.oldCount}
                />
              </React.Fragment>
            ))}
            {!!temporaryDiscountAmount && (
              <div className="single-bundle-card__ticket-discount">
                {icons.discountIcon} £{temporaryDiscountAmount} off
              </div>
            )}
            {ticketSections.length > 1 && (
              <p className="single-bundle-card__total-tickets">
                Total tickets:{" "}
                {totalTickets * (isDouble ? 2 : 1) +
                  (hasLoyaltyExtras ? loyaltyBonusCount : 0)}
              </p>
            )}
          </div>

          <div className="single-bundle-card__price-block">
            <div className="single-bundle-card__price-info">
              <BundlePrice
                isLandingTicket={isLandingTicket}
                landingTicket={landingTicket}
                finalPrice={finalPrice}
                newPrice={finalPrice}
                oldPrice={oldPrice}
                formattedFinal={formattedFinal}
                isDouble={isDouble}
                is341={is341}
                loyaltyTickets={loyaltyBonusCount}
                ticketsAmount={totalPrizeTickets}
                baseTicketsAmount={totalTickets}
              />
            </div>
            <button
              id={buttonId}
              type="button"
              className="single-bundle-card__price-button"
              disabled={isDisabled}
            >
              {loading && isBundleChosen ? 'Adding...' : 'Buy now'}
            </button>
          </div>
        </div>
      </div>
      {is341 && (
        <div className="single-bundle-card__value-tag" aria-hidden="true">
          3 for 1 value
        </div>
      )}
    </div>
  );
};

export default SingleBundleCard;
